import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Copy, Gift, Users, Check, Storefront, Link as LinkIcon, CurrencyDollar } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralPage() {
  const { user } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchReferral = async () => {
      try {
        const { data } = await axios.get(`${API}/user/referral`, { withCredentials: true });
        setReferralData(data);
      } catch (err) {
        toast.error('Failed to load referral info');
      } finally {
        setLoading(false);
      }
    };
    fetchReferral();
  }, []);

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {}).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  };

  const copyCode = () => {
    if (!referralData) return;
    copyToClipboard(referralData.referral_code);
    setCopied(true);
    toast.success('Referral code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    if (!referralData) return;
    const link = `${window.location.origin}/register?ref=${referralData.referral_code}`;
    copyToClipboard(link);
    toast.success('Referral link copied!');
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
      <div className="text-xl font-bold text-[#004E89]">Loading...</div>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F6] px-4 py-8" data-testid="referral-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-black text-[#004E89] mb-2" style={{ fontFamily: 'Outfit' }}>
          Refer &amp; Earn
        </h1>
        <p className="text-[#5C7A99] mb-8">Share your code — both you and your friend get a free listing!</p>

        {/* Your Code */}
        <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89] mb-6" data-testid="referral-code-card">
          <div className="flex items-center gap-2 mb-4">
            <Gift size={24} weight="duotone" className="text-[#FF6B35]" />
            <h2 className="text-xl font-bold text-[#004E89]" style={{ fontFamily: 'Outfit' }}>Your Referral Code</h2>
          </div>

          <div className="bg-[#F9F9F6] border-2 border-dashed border-[#004E89] rounded-xl p-4 flex items-center justify-between mb-4">
            <span className="text-2xl font-black text-[#004E89] tracking-wider" style={{ fontFamily: 'Outfit' }} data-testid="referral-code-display">
              {referralData?.referral_code || '...'}
            </span>
            <button onClick={copyCode} className="btn-primary py-2 px-4 text-sm flex items-center gap-2" data-testid="copy-code-btn">
              {copied ? <Check size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          <button onClick={copyLink} className="btn-secondary w-full py-3 text-sm flex items-center justify-center gap-2" data-testid="copy-link-btn">
            <LinkIcon size={18} weight="bold" />
            Copy Referral Link
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-5 shadow-[4px_4px_0px_#004E89] text-center" data-testid="referral-count-card">
            <Users size={28} weight="duotone" className="text-[#FF6B35] mx-auto mb-2" />
            <div className="text-3xl font-black text-[#004E89]" style={{ fontFamily: 'Outfit' }}>{referralData?.referral_count || 0}</div>
            <p className="text-xs font-bold text-[#5C7A99] uppercase tracking-wide mt-1">Referred</p>
          </div>
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-5 shadow-[4px_4px_0px_#004E89] text-center" data-testid="free-listings-card">
            <Storefront size={28} weight="duotone" className="text-[#4CB944] mx-auto mb-2" />
            <div className="text-3xl font-black text-[#004E89]" style={{ fontFamily: 'Outfit' }}>{referralData?.free_listings || 0}</div>
            <p className="text-xs font-bold text-[#5C7A99] uppercase tracking-wide mt-1">Free Listings</p>
          </div>
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-5 shadow-[4px_4px_0px_#004E89] text-center" data-testid="referral-discount-card">
            <CurrencyDollar size={28} weight="duotone" className="text-[#FFD166] mx-auto mb-2" />
            <div className="text-3xl font-black text-[#004E89]" style={{ fontFamily: 'Outfit' }}>${referralData?.referral_discount_credits || 0}</div>
            <p className="text-xs font-bold text-[#5C7A99] uppercase tracking-wide mt-1">Pro Discount</p>
          </div>
        </div>

        {/* Referral Discount Progress */}
        <div className="bg-gradient-to-r from-[#FFD166] to-[#FF6B35] border-2 border-[#004E89] rounded-2xl p-5 shadow-[4px_4px_0px_#004E89] mb-6" data-testid="referral-discount-progress">
          <div className="flex items-center gap-2 mb-3">
            <CurrencyDollar size={22} weight="bold" className="text-[#004E89]" />
            <h3 className="text-base font-bold text-[#004E89]" style={{ fontFamily: 'Outfit' }}>Pro Discount Progress</h3>
          </div>
          <p className="text-sm text-[#004E89] mb-3">
            Every <strong>25 referrals</strong> earns you <strong>$5 off</strong> your next Pro subscription month!
          </p>
          <div className="bg-white bg-opacity-50 rounded-full h-4 border-2 border-[#004E89] overflow-hidden">
            <div
              className="h-full bg-[#004E89] rounded-full transition-all duration-500"
              style={{ width: `${((referralData?.referral_count || 0) % 25) / 25 * 100}%` }}
            />
          </div>
          <p className="text-xs font-bold text-[#004E89] mt-2">
            {(referralData?.referral_count || 0) % 25} / 25 referrals — {referralData?.next_milestone || (25 - ((referralData?.referral_count || 0) % 25))} more to next $5 discount
          </p>
        </div>

        {/* How it works */}
        <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89] mb-6">
          <h3 className="text-lg font-bold text-[#004E89] mb-4" style={{ fontFamily: 'Outfit' }}>How It Works</h3>
          <div className="space-y-4">
            {[
              { step: '1', title: 'Share your code', desc: 'Send your unique referral code or link to friends' },
              { step: '2', title: 'They sign up', desc: 'Your friend creates an account using your code' },
              { step: '3', title: 'Both earn rewards', desc: 'You each get 1 free listing ($3 value)' },
              { step: '4', title: 'Hit 25 referrals', desc: 'Earn $5 off your next Pro subscription month!' },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#FFD166] border-2 border-[#004E89] flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-black text-[#004E89]">{item.step}</span>
                </div>
                <div>
                  <p className="font-bold text-[#004E89] text-sm">{item.title}</p>
                  <p className="text-xs text-[#5C7A99]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Referral History */}
        {referralData?.referrals?.length > 0 && (
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]" data-testid="referral-history">
            <h3 className="text-lg font-bold text-[#004E89] mb-4" style={{ fontFamily: 'Outfit' }}>Referral History</h3>
            <div className="space-y-3">
              {referralData.referrals.map((ref, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-[#004E89]">{ref.referred_email}</p>
                    <p className="text-xs text-[#5C7A99]">{new Date(ref.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="tag tag-garage text-[10px]">+1 free listing</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
